import { Button, Chip, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";

import Page from "~/components/Page";
import { useTrans } from '~/hooks/useTrans';
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IQuickCommentItem } from "~/providers/admin-interfaces";
import { apiCreateProjectAdminComment, apiSearchQuickComment } from "~/providers/api";
import Loader from "~/components/Loader";
import AdminQuickCommentsTable from "~/tables/AdminQuickCommentsTable";
import { MESSAGE } from "~/const";
import { parseQuickComment } from "~/utils/utils";
import { getLink } from "~/providers/route";
import { IQuickCommentParseData } from "~/interfaces";

import "./style.less";

interface IState {
	items: Array<IQuickCommentItem>;
	loading: boolean;
	disabled: boolean;
	comment: string;
	commentError: string;
	errorMsg: string;
	parseData: IQuickCommentParseData;
}

const DEBUG = false;
const INIT_STATE: IState = {
	items: [],
	loading: false,
	disabled: false,
	comment: DEBUG
		? MESSAGE
		: "",
	commentError: "",
	errorMsg: "",
	parseData: null,
};
const MIN_CHARS = 5;

export default function AdminQuickCommentPage() {
	const navigate = useNavigate();
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		...INIT_STATE,
	});

	async function parseComment(comment: string) {
		const quickCommentData = parseQuickComment(comment);

		if (quickCommentData.empty) {
			updateState({
				parseData: null,
				errorMsg: trans("adminQuickCommentPage.noRegValues"),
			});
		} else {
			updateState({
				loading: true,
				errorMsg: "",
				items: [],
				parseData: quickCommentData,
			});

			// http://mapy-developer-api.master.ops.dszn.cz/v1/admin/docs#tag/Project-Metadata/operation/getAllProjectsMetadata
			const searchData = await apiSearchQuickComment({
				apiKeys: quickCommentData.apiKeysIds,
				emails: quickCommentData.emails,
				projectOrApiKeyNames: quickCommentData.apiKeysNames,
			}, lang);

			if (searchData.errors.length) {
				updateState({
					loading: false,
					errorMsg: searchData.errors.join(", "),
				});
			} else {
				updateState({
					items: searchData.items,
					loading: false,
					errorMsg: searchData.items.length === 0
						? trans("adminQuickCommentPage.emptyResponse")
						: "",
				});
			}
		}
	}

	function parseCommentClick() {
		parseComment(state.comment);
	}

	async function onCommentClick(item: IQuickCommentItem) {
		if (state.comment.length < MIN_CHARS) {
			updateState({
				commentError: trans("adminQuickCommentPage.minError", { count: MIN_CHARS }),
			});

			return;
		}

		updateState({
			loading: true,
			disabled: true,
			errorMsg: "",
			commentError: "",
		});

		// pridame comment
		const addCommentData = await apiCreateProjectAdminComment(item.projectId, {
			comment: state.comment.replaceAll("\n", "<br />"),
		}, lang);

		if (addCommentData.noErrors) {
			// prejdeme na projekt
			const link = getLink("project-business", true, {
				projectId: item.projectId,
			});

			navigate(link);
		} else {
			updateState({
				loading: false,
				disabled: false,
				errorMsg: addCommentData.errors.join(", "),
			});
		}
	}

	function onLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			const usp = new URLSearchParams(location.search);
			const comment = decodeURIComponent(usp.get("text") || "");

			if (comment) {
				updateState({
					comment,
				});
				parseComment(comment);
			}
		}
	}

	return <Page title={trans("menu.adminQuickComment")} breadcrumbs={[trans("administration"), trans("menu.adminQuickComment")]} checkInternalRole="salesman" isDirty={false} seoTitleTrans="menu.adminQuickComment" onLoad={onLoad}>
		<div className="adminQuickCommentPage__content">
			<div className="adminQuickCommentPage__commentSection">
				<TextField
					label={trans("adminQuickCommentPage.comment")}
					value={state.comment}
					style={{ width: "100%" }}
					multiline={true}
					onChange={event => updateState({ comment: event.target.value })}
					color="success"
					autoComplete="off"
					rows={10}
					error={state.commentError !== ""}
					helperText={state.commentError}
				/>
			</div>
			{ state.parseData && <div className="adminQuickCommentPage__debugCont">
				<div className="adminQuickCommentPage__debug">
					API key ids: { state.parseData.apiKeysIds.map(item => <Chip label={item} variant="outlined" key={item} />) }{ state.parseData.apiKeysIds.length === 0 && <span>prázdné</span> }
				</div>
				<div className="adminQuickCommentPage__debug">
					API key names: { state.parseData.apiKeysNames.map(item => <Chip label={item} variant="outlined" key={item} />) }{ state.parseData.apiKeysNames.length === 0 && <span>prázdné</span> }
				</div>
				<div className="adminQuickCommentPage__debug">
					Emails: { state.parseData.emails.map(item => <Chip label={item} variant="outlined" key={item} />) }{ state.parseData.emails.length === 0 && <span>prázdné</span> }
				</div>
			</div> }
			<div className="adminQuickCommentPage__buttons">
				<Button color="success" variant="contained" onClick={parseCommentClick}>
					{ trans("adminQuickCommentPage.parse") }
				</Button>
			</div>
			{ state.loading && <Loader msg={trans("loading")} /> }
			{ state.items.length > 0 && <div className="adminQuickCommentPage__results">
				<h3 className="adminQuickCommentPage__resultsTitle">{ trans("adminQuickCommentPage.results") }:</h3>
				<AdminQuickCommentsTable items={state.items} disabled={state.disabled} onClick={onCommentClick} />
			</div> }
		</div>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
	</Page>;
}
